import React, { useState, useEffect } from 'react';
import { Mountain, MapPin, AlertCircle, ChevronDown, Sparkles, Users, BookOpen, ExternalLink, Camera, Shield, TrendingUp, Leaf } from 'lucide-react';

export default function App() {
  const [scrollY, setScrollY] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const parallaxOffset = scrollY * 0.5;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Mountain },
    { id: 'gallery', label: 'Gallery', icon: Camera },
    { id: 'biology', label: 'Biology & Behavior', icon: Sparkles },
    { id: 'resources', label: 'Resources', icon: BookOpen }
  ];

  const galleryImages = [
    { url: './mtn-goat-baby.webp', caption: 'Baby mountain goat' },
    { url: './mtn-goat-cliff.webp', caption: 'Adult mountain goat navigating steep terrain' },
    { url: './mtn-goat-with-baby.webp', caption: 'Mountain goat with thick winter coat' }
  ];

  const resources = [
    {
      name: 'National Park Service',
      description: 'Official information on mountain goats in national parks',
      url: 'https://www.nps.gov/kefj/learn/nature/mountain-goat.htm',
      type: 'Government'
    },
    {
      name: 'U.S. Fish & Wildlife Service',
      description: 'Wildlife management and species information',
      url: 'https://www.fws.gov/species/rocky-mountain-goat-oreamnos-americanus',
      type: 'Government'
    }
  ];

  return (
    <div className="bg-slate-900 text-white min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0 bg-cover bg-center will-change-transform"
          style={{
            backgroundImage: 'linear-gradient(rgba(15, 23, 42, 0.6), rgba(15, 23, 42, 0.8)), url(./mountain-goat.webp)',
            transform: `translateY(${parallaxOffset}px)`,
          }}
        />

        <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
          <div className="inline-block mb-6">
            <Mountain className="w-20 h-20 text-cyan-400 animate-pulse" />
          </div>
          <h1 className="text-7xl md:text-9xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 bg-clip-text text-transparent">
            Mountain Goats
          </h1>
          <p className="text-2xl md:text-4xl text-slate-300 mb-6 font-light">
            Oreamnos americanus
          </p>
          <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-12 leading-relaxed">
            Explore the remarkable alpine specialists that have mastered life at extreme altitudes
          </p>
          <ChevronDown className="w-12 h-12 mx-auto text-cyan-400 animate-bounce" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none" />
      </section>

      {/* Navigation Tabs */}
      <nav className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab.id
                    ? 'text-cyan-400 border-b-2 border-cyan-400'
                    : 'text-slate-400 hover:text-slate-300'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 mb-20">
              <div className="space-y-6">
                <h2 className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  The G.O.A.T. of Goats
                </h2>
                <p className="text-lg text-slate-300 leading-relaxed">
                  Despite their name, mountain goats (Oreamnos americanus) are not true goats. They belong to the subfamily Caprinae, making them more closely related to antelopes and chamois than to domestic goats. This taxonomic distinction reflects their unique evolutionary path in adapting to North America's most challenging alpine environments.
                </p>
                <p className="text-lg text-slate-300 leading-relaxed">
                  These remarkable animals are the largest mammals that routinely live at high altitudes in North America. They occupy a specialized ecological niche where their extraordinary climbing abilities provide both access to food and protection from most predators.
                </p>
              </div>
              <div className="relative">
                <img
                  src="./mtn-goat-cliff.webp"
                  alt="Mountain goat on cliff"
                  className="w-full h-full object-cover rounded-2xl shadow-2xl"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
              {[
                { label: 'Elevation Range', value: '6,500-13,000 ft', icon: TrendingUp, color: 'cyan' },
                { label: 'Average Weight', value: '125-150 lbs', icon: Sparkles, color: 'blue' },
                { label: 'Lifespan', value: '12-15 years', icon: Leaf, color: 'purple' },
                { label: 'Population', value: '~100,000', icon: Users, color: 'pink' }
              ].map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div key={i} className={`bg-slate-800/50 backdrop-blur p-6 rounded-xl border border-${stat.color}-500/20 hover:border-${stat.color}-500/50 transition-all duration-300 group`}>
                    <Icon className={`w-8 h-8 text-${stat.color}-400 mb-3 group-hover:scale-110 transition-transform`} />
                    <div className={`text-3xl font-bold text-${stat.color}-400 mb-2`}>{stat.value}</div>
                    <div className="text-slate-400 text-sm">{stat.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="bg-gradient-to-br from-slate-800/50 to-slate-800/30 backdrop-blur rounded-2xl p-8 border border-slate-700/50">
              <MapPin className="w-10 h-10 text-cyan-400 mb-4" />
              <h3 className="text-3xl font-bold mb-4 text-cyan-400">Geographic Distribution</h3>
              <p className="text-lg text-slate-300 leading-relaxed mb-4">
                Mountain goats are native to the mountainous regions of western North America, with populations ranging from southeastern Alaska through the Yukon and Northwest Territories, British Columbia, Alberta, Washington, Idaho, and Montana. Smaller populations exist in Wyoming, Colorado, South Dakota, and Oregon.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                They have also been successfully introduced to states like Nevada, Utah, and New Mexico, where they thrive in similar alpine habitats. Their range expansion is carefully managed to prevent negative impacts on existing ecosystems.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                Photo Gallery
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                Witness the beauty and majesty of mountain goats in their natural alpine habitat
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((img, i) => (
                <div
                  key={i}
                  onClick={() => setSelectedImage(img)}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/20"
                >
                  <img
                    src={img.url}
                    alt={img.caption}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="text-white font-medium">{img.caption}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Biology & Behavior Tab */}
      {activeTab === 'biology' && (
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto space-y-16">
            <div>
              <h2 className="text-5xl font-bold mb-12 bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent text-center">
                Physical Adaptations
              </h2>

              <div className="grid lg:grid-cols-2 gap-8">
                <div className="bg-slate-800/40 backdrop-blur rounded-2xl p-8 border border-slate-700/50">
                  <h3 className="text-2xl font-bold text-cyan-400 mb-4">Specialized Hooves</h3>
                  <p className="text-slate-300 leading-relaxed mb-4">
                    Mountain goat hooves are engineering marvels. Each hoof consists of two toes that can spread apart to improve balance and distribute weight across rocky surfaces. The outer shell is hard and sharp, providing grip on rock edges, while the inner pad is soft and rubbery, creating suction and friction on smooth surfaces.
                  </p>
                  <p className="text-slate-300 leading-relaxed">
                    This unique structure allows them to navigate near-vertical terrain that would be impossible for most animals. They can even climb trees and stand on ledges barely wider than their hooves.
                  </p>
                </div>

                <div className="bg-slate-800/40 backdrop-blur rounded-2xl p-8 border border-slate-700/50">
                  <h3 className="text-2xl font-bold text-blue-400 mb-4">Insulating Coat</h3>
                  <p className="text-slate-300 leading-relaxed mb-4">
                    Their coat features two distinct layers: a dense woolly undercoat that provides insulation, and longer guard hairs that shed water and snow. This double-layer system can be up to 6 inches thick in winter, protecting them from temperatures as low as -50°F and winds exceeding 100 mph.
                  </p>
                  <p className="text-slate-300 leading-relaxed">
                    The white coloration provides camouflage against snow and reflects heat during intense summer sun at high altitudes.
                  </p>
                </div>

                <div className="bg-slate-800/40 backdrop-blur rounded-2xl p-8 border border-slate-700/50">
                  <h3 className="text-2xl font-bold text-purple-400 mb-4">Muscular Build</h3>
                  <p className="text-slate-300 leading-relaxed mb-4">
                    Mountain goats possess exceptionally powerful shoulder and leg muscles that generate tremendous force. This strength enables them to leap up to 12 feet in a single bound and climb slopes with grades exceeding 60 degrees.
                  </p>
                  <p className="text-slate-300 leading-relaxed">
                    Males (billies) are significantly larger than females (nannies), with adult males weighing 200-300 pounds compared to females at 125-180 pounds.
                  </p>
                </div>

                <div className="bg-slate-800/40 backdrop-blur rounded-2xl p-8 border border-slate-700/50">
                  <h3 className="text-2xl font-bold text-pink-400 mb-4">Sensory Systems</h3>
                  <p className="text-slate-300 leading-relaxed mb-4">
                    Their eyes are positioned laterally on their head, providing nearly 360-degree vision to detect predators. This panoramic view is crucial in open alpine environments where cover is minimal.
                  </p>
                  <p className="text-slate-300 leading-relaxed">
                    They also possess an acute sense of smell and hearing, allowing them to detect threats like mountain lions, bears, and golden eagles from considerable distances.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Resources Tab */}
      {activeTab === 'resources' && (
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <BookOpen className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
              <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                Learn More
              </h2>
              <p className="text-lg text-slate-400 max-w-3xl mx-auto">
                Discover more about mountain goats from reputable organizations dedicated to conservation and education.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-16">
              {resources.map((resource, i) => (
                <a
                  key={i}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-slate-800/40 backdrop-blur rounded-xl p-6 border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-slate-200 group-hover:text-cyan-400 transition-colors mb-2">
                        {resource.name}
                      </h3>
                      <span className="inline-block px-3 py-1 text-xs font-semibold bg-cyan-500/20 text-cyan-400 rounded-full">
                        {resource.type}
                      </span>
                    </div>
                    <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-cyan-400 transition-colors" />
                  </div>
                  <p className="text-slate-300 leading-relaxed">
                    {resource.description}
                  </p>
                </a>
              ))}
            </div>

            <div className="bg-gradient-to-br from-cyan-900/20 to-purple-900/20 backdrop-blur rounded-2xl p-8 border border-cyan-500/30">
              <h3 className="text-3xl font-bold mb-4 text-cyan-400">Want to see mountain goats?</h3>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Interested in seeing mountain goats in their natural habitat? Many national parks and wilderness areas offer opportunities to observe these incredible animals. Always maintain a safe distance and follow park guidelines.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://www.nps.gov/findapark/index.htm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold rounded-lg transition-colors duration-300"
                >
                  Find National Parks
                </a>
                <a
                  href="https://www.fws.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors duration-300"
                >
                  Wildlife Resources
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/95 backdrop-blur-lg flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-6xl w-full">
            <img
              src={selectedImage.url}
              alt={selectedImage.caption}
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
            <p className="text-center text-xl text-slate-300 mt-6">{selectedImage.caption}</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-16 px-4 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto flex flex-col gap-8">
          <div className="text-center mb-8">
            <Mountain className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-slate-200 mb-2">Mountain Goats Are Cool</h3>
            <p className="text-slate-400 max-w-2xl mx-auto">
              This is an educational resource dedicated to sharing knowledge about mountain goats and their remarkable alpine lifestyle.
            </p>
          </div>

          <div className="pt-8 border-t border-slate-800 text-center text-slate-500">
            <p>© {(new Date().getFullYear())}, All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}